import type {
  OutgoingStatisticRow,
  StatisticDisplayMode,
  StatisticModeOption,
} from './statisticRowTypes';
import type { ScenarioVariant } from './scenarioMetadata';
import { formatCount, formatPercent, safeRatio } from './statisticsFormatters';
import { getOutgoingStatisticTooltip } from './outgoingStatisticTooltips';
import {
  createStatisticCountCell,
  createStatisticPercentCell,
  createStatisticRatioCell,
} from '../shared/statisticCells';

interface MetricRowInput {
  readonly key: string;
  readonly section: string;
  readonly parameter: string;
  readonly value: number;
  readonly denominator: number | null | undefined;
  readonly level?: number;
  readonly variant?: ScenarioVariant;
  readonly children?: OutgoingStatisticRow[];
  readonly isMuted?: boolean;
  readonly isEmphasized?: boolean;
}

interface RatioRowInput {
  readonly key: string;
  readonly section: string;
  readonly parameter: string;
  readonly numerator: number;
  readonly denominator: number | null | undefined;
  readonly level?: number;
}

interface SectionRowInput {
  readonly key: string;
  readonly section: string;
  readonly children: OutgoingStatisticRow[];
  readonly subtitle?: string;
  readonly variant?: ScenarioVariant;
  readonly childrenByMode?: Record<
    StatisticDisplayMode,
    OutgoingStatisticRow[]
  >;
  readonly modeOptions?: readonly StatisticModeOption[];
  readonly defaultMode?: StatisticDisplayMode;
  readonly tooltip?: string;
}

interface EmptyRowInput {
  readonly key: string;
  readonly section: string;
  readonly parameter: string;
  readonly level?: number;
}

export const createMetricRow = ({
  key,
  section,
  parameter,
  value,
  denominator,
  level = 0,
  variant,
  children,
  isMuted,
  isEmphasized,
}: MetricRowInput): OutgoingStatisticRow => ({
  key,
  section,
  parameter,
  value: formatCount(value),
  percent: formatPercent(value, denominator, variant === 'informing' ? 1 : 2),
  cells: {
    value: createStatisticCountCell(value),
    percent: createStatisticPercentCell(
      value,
      denominator,
      variant === 'informing' ? 1 : 2,
    ),
  },
  level,
  variant,
  children,
  isMuted,
  isEmphasized,
  tooltip: getOutgoingStatisticTooltip(key),
});

export const createFixedPercentMetricRow = ({
  key,
  section,
  parameter,
  value,
  level = 0,
}: Omit<MetricRowInput, 'denominator'>): OutgoingStatisticRow => ({
  key,
  section,
  parameter,
  value: formatCount(value),
  percent: '100.00%',
  cells: {
    value: createStatisticCountCell(value),
    percent: { value: 1, numFmt: '0.00%' },
  },
  level,
  tooltip: getOutgoingStatisticTooltip(key),
});

export const createZeroPercentMetricRow = ({
  key,
  section,
  parameter,
  value,
  level = 0,
}: Omit<MetricRowInput, 'denominator'>): OutgoingStatisticRow => ({
  key,
  section,
  parameter,
  value: formatCount(value),
  percent: '0.00%',
  cells: {
    value: createStatisticCountCell(value),
    percent: { value: 0, numFmt: '0.00%' },
  },
  level,
  tooltip: getOutgoingStatisticTooltip(key),
});

export const createUnavailableRow = ({
  key,
  section,
  parameter,
  level = 0,
}: EmptyRowInput): OutgoingStatisticRow => ({
  key,
  section,
  parameter,
  value: '—',
  percent: '—',
  cells: { value: { value: '—' }, percent: { value: '—' } },
  level,
  tooltip: getOutgoingStatisticTooltip(key),
});

export const createRatioRow = ({
  key,
  section,
  parameter,
  numerator,
  denominator,
  level = 0,
}: RatioRowInput): OutgoingStatisticRow => ({
  key,
  section,
  parameter,
  value: safeRatio(numerator, denominator),
  percent: formatPercent(denominator ? denominator : 0, denominator),
  cells: {
    value: createStatisticRatioCell(numerator, denominator),
    percent: createStatisticPercentCell(
      denominator ? denominator : 0,
      denominator,
    ),
  },
  level,
  tooltip: getOutgoingStatisticTooltip(key),
});

export const createSectionRow = ({
  key,
  section,
  children,
  subtitle,
  variant,
  childrenByMode,
  modeOptions,
  defaultMode,
  tooltip,
}: SectionRowInput): OutgoingStatisticRow => ({
  key,
  section,
  parameter: section,
  value: '',
  percent: '',
  level: 0,
  children,
  subtitle,
  variant,
  childrenByMode,
  modeOptions,
  defaultMode,
  tooltip,
});
